/**
 * The two interactive slide layouts (plan docs/plans/lms-plan.md 6.1, 6.2
 * rules 4 and 5, 7.2, 18.11; Appendix E): the knowledge check (an ungraded
 * question with immediate feedback; it must be answered to advance) and the
 * scenario (a named role in a specific situation, a decision, feedback on
 * the choice).
 *
 * Both slides keep their correct / best flags in the content response
 * (18.11), so the feedback is shown here without a server round trip, and
 * the answer is never stored as a record (7.2: only slides_seen is).
 *
 * The feedback region is aria-live (section 13) and every option says in
 * words whether it was the right one, so color never carries the meaning.
 */
import { CheckCircle2, UserRound, XCircle } from 'lucide-react';
import type { KnowledgeCheckSlide, ScenarioSlide } from '../../content/types';
import type { SlideAnswer } from '../../hooks/usePlayer';
import { primaryBtnCls } from '../../../ui/styles';
import { MdLite } from '../../content/MdLite';
import { bodyCls, headingCls, microLabelCls, noticeCls, optionCls, pillCls } from './styles';

const KIND_HINT: Record<KnowledgeCheckSlide['kind'], string> = {
  single: 'Choose one answer.',
  true_false: 'Choose true or false.',
  multi: 'Choose every answer that applies.',
};

export interface KnowledgeCheckProps {
  slide: KnowledgeCheckSlide;
  answer: SlideAnswer | undefined;
  onSelect: (optionId: string) => void;
  onCheck: () => void;
}

export function KnowledgeCheck({ slide, answer, onSelect, onCheck }: KnowledgeCheckProps) {
  const multi = slide.kind === 'multi';
  const selected = answer?.selected ?? [];
  const checked = answer?.checked === true;
  const options = Array.isArray(slide.options) ? slide.options : [];
  const correctIds: string[] = options.filter((o) => o.correct === true).map((o) => o.id);
  const right =
    checked && selected.length === correctIds.length && selected.every((id) => correctIds.includes(id));
  const groupName = `lms-kc-${slide.id}`;

  return (
    <div className="space-y-4" data-testid="lms-knowledge-check" data-slide-id={slide.id}>
      <div className="space-y-1">
        <div className={microLabelCls}>Check your understanding</div>
        {slide.heading && <h2 className={headingCls}>{slide.heading}</h2>}
        <p className={`${bodyCls} font-medium text-gray-900 dark:text-gray-100`} id={`${groupName}-stem`}>
          {slide.stem}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{KIND_HINT[slide.kind] ?? KIND_HINT.single}</p>
      </div>

      <fieldset aria-labelledby={`${groupName}-stem`} className="space-y-2">
        {options.map((option) => {
          const isSelected = selected.includes(option.id);
          const isCorrect = option.correct === true;
          const state = !checked ? (isSelected ? 'selected' : 'idle') : isCorrect ? 'correct' : isSelected ? 'incorrect' : 'muted';
          return (
            <label key={option.id} className={optionCls(state)} data-testid="lms-kc-option" data-option-id={option.id}>
              <input
                type={multi ? 'checkbox' : 'radio'}
                name={groupName}
                value={option.id}
                checked={isSelected}
                disabled={checked}
                onChange={() => onSelect(option.id)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
              />
              <span className="min-w-0 flex-1">
                <span className="block">{option.text}</span>
                {checked && (
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    {isCorrect && <span className={pillCls('green')}>Correct answer</span>}
                    {isSelected && <span className={pillCls(isCorrect ? 'green' : 'red')}>Your answer</span>}
                  </span>
                )}
                {checked && option.feedback && (
                  <span className="mt-1 block text-xs text-gray-600 dark:text-gray-300" data-testid="lms-kc-feedback">
                    {option.feedback}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </fieldset>

      <div aria-live="polite" data-testid="lms-kc-live">
        {checked ? (
          <div className={noticeCls(right ? 'green' : 'amber')} data-testid="lms-kc-result">
            {right ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <div className="font-semibold">{right ? 'Correct.' : 'Not quite.'}</div>
              {slide.explanation && <div className="mt-0.5">{slide.explanation}</div>}
            </div>
          </div>
        ) : null}
      </div>

      {!checked && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={primaryBtnCls}
            disabled={selected.length === 0}
            onClick={onCheck}
            data-testid="lms-kc-check"
          >
            Check answer
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">Answer this check to continue.</span>
        </div>
      )}
    </div>
  );
}

export interface ScenarioProps {
  slide: ScenarioSlide;
  answer: SlideAnswer | undefined;
  onChoose: (optionId: string) => void;
}

export function Scenario({ slide, answer, onChoose }: ScenarioProps) {
  const chosen = answer?.checked ? answer.selected[0] ?? null : null;
  const options = Array.isArray(slide.options) ? slide.options : [];
  const chosenOption = options.find((o) => o.id === chosen) ?? null;
  const best = chosenOption?.best === true;

  return (
    <div className="space-y-4" data-testid="lms-scenario" data-slide-id={slide.id}>
      <div className="space-y-2">
        <div className={microLabelCls}>Scenario</div>
        <h2 className={headingCls}>{slide.heading}</h2>
        {slide.role && (
          <span className={pillCls('gray')}>
            <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
            {slide.role}
          </span>
        )}
      </div>
      <MdLite source={slide.situation_md} size="base" />
      <p className={`${bodyCls} font-medium text-gray-900 dark:text-gray-100`} id={`lms-scenario-${slide.id}-q`}>
        {slide.question}
      </p>
      <div role="group" aria-labelledby={`lms-scenario-${slide.id}-q`} className="space-y-2">
        {options.map((option) => {
          const isChosen = option.id === chosen;
          const isBest = option.best === true;
          const state = chosen === null ? 'idle' : isBest ? 'correct' : isChosen ? 'incorrect' : 'muted';
          return (
            <button
              key={option.id}
              type="button"
              className={optionCls(state)}
              disabled={chosen !== null}
              aria-pressed={isChosen}
              onClick={() => onChoose(option.id)}
              data-testid="lms-scenario-option"
              data-option-id={option.id}
            >
              <span className="min-w-0 flex-1">
                <span className="block">{option.text}</span>
                {chosen !== null && (
                  <span className="mt-1 flex flex-wrap items-center gap-1.5">
                    {isBest && <span className={pillCls('green')}>Best choice</span>}
                    {isChosen && <span className={pillCls(isBest ? 'green' : 'amber')}>Your choice</span>}
                  </span>
                )}
                {chosen !== null && option.feedback && (
                  <span className="mt-1 block text-xs text-gray-600 dark:text-gray-300">{option.feedback}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      <div aria-live="polite" data-testid="lms-scenario-live">
        {chosenOption ? (
          <div className={noticeCls(best ? 'green' : 'amber')}>
            {best ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <div className="font-semibold">{best ? 'That is the best choice.' : 'There is a better choice.'}</div>
              {chosenOption.feedback && <div className="mt-0.5">{chosenOption.feedback}</div>}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
